import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CreateUserAdminForm.css";
import axios from "axios";

const AssistanteCreateCommercialForm = ({ onClose, initialData, additionalButtons, user, onSubmit }) => {
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    statut: "commercial",
    ville: initialData?.ville || "",
    siret: initialData?.siret || "",
    siren: initialData?.siren || "",
    telephone: initialData?.telephone || "",
    service_principal: "",
    groupe_id: user?.groupe_id || 0,
    concessions_id: initialData?.concessions_id || 0,
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [concessions, setConcessions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [copySuccess, setCopySuccess] = useState({
    identifier: false,
    password: false
  });
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

  useEffect(() => {
    // Ajouter la classe modal-open au body quand le modal est ouvert
    document.body.classList.add('modal-open');
    
    // Nettoyer en supprimant la classe quand le composant est démonté
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    // Set the groupe_id from user prop when component mounts
    if (user && user.groupe_id) {
      setFormData(prev => ({
        ...prev,
        groupe_id: user.groupe_id
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchConcessions = async () => {
      try {
        const token = localStorage.getItem("token");
        
        const response = await axios.get(`${API_URL}/concessions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data) {
          // Filter concessions by groupe_id
          const filteredConcessions = response.data.filter(
            (concession) => concession.groupe_id == formData.groupe_id // Using == for type coercion
          );

          setConcessions(filteredConcessions);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des concessions:", error);
        setError("Erreur lors de la récupération des concessions");
      }
    };

    fetchConcessions();
  }, [API_URL, formData.groupe_id]);
  useEffect(() => {
    if (!formData.concessions_id || concessions.length === 0) return;
  
    const selectedConcession = concessions.find(
      (c) => c.id === parseInt(formData.concessions_id)
    );
  
    if (selectedConcession && selectedConcession.siret) {
      const siret = selectedConcession.siret;
      const siren = siret.substring(0, 9);
  
      setFormData((prev) => ({
        ...prev,
        siret,
        siren
      }));
    }
  }, [formData.concessions_id, concessions]);
  
  
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    // Vérification de l'email
    if (!formData.email) {
      errors.email = "L'email est requis";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "L'email est invalide";
      isValid = false;
    }
    
    // Vérification du prénom
    if (!formData.firstName || formData.firstName.trim() === "") {
      errors.firstName = "Le prénom est requis";
      isValid = false;
    }
    
    // Vérification du nom
    if (!formData.lastName || formData.lastName.trim() === "") {
      errors.lastName = "Le nom est requis";
      isValid = false;
    }
    
    // Vérification de la ville
    if (!formData.ville || formData.ville.trim() === "") {
      errors.ville = "La ville est requise";
      isValid = false;
    }
    
    // Vérification du SIRET
    if (!formData.siret) {
      errors.siret = "Le SIRET est requis";
      isValid = false;
    } else if (formData.siret.length !== 14) {
      errors.siret = "Le SIRET doit contenir 14 chiffres";
      isValid = false;
    }
    
    // Vérification du SIREN
    if (!formData.siren) {
      errors.siren = "Le SIREN est requis";
      isValid = false;
    } else if (formData.siren.length !== 9) {
      errors.siren = "Le SIREN doit contenir 9 chiffres";
      isValid = false;
    }
    
    // Vérification du téléphone
    if (!formData.telephone) {
      errors.telephone = "Le téléphone est requis";
      isValid = false;
    } else if (formData.telephone.length !== 10) {
      errors.telephone = "Le téléphone doit contenir 10 chiffres";
      isValid = false;
    }
    
    // Vérification de la concession
    if (!formData.concessions_id) {
      errors.concessions_id = "La concession est requise";
      isValid = false;
    }
    
    setFieldErrors(errors);
    
    if (!isValid) {
      setError("Veuillez corriger les erreurs dans le formulaire");
    }
    
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Always clear the general error when user makes changes
    setError(null);
    
    // Clear field-specific errors when the field is modified
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Validation spécifique pour les champs numériques
    if (name === 'telephone' || name === 'siret' || name === 'siren') {
      // Vérifier que la valeur ne contient que des chiffres
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: `Le champ ${name} ne doit contenir que des chiffres`
        }));
        return;
      }
      
      // Limiter la longueur et valider le format
      if (name === 'telephone') {
        if (value.length > 10) {
          return;
        }
        if (value.length > 0 && value.length < 10) {
          setFieldErrors(prev => ({
            ...prev,
            [name]: "Le numéro de téléphone doit contenir 10 chiffres"
          }));
        } else {
          setFieldErrors(prev => ({
            ...prev,
            [name]: null
          }));
        }
      }
      
      if (name === 'siret') {
        if (value.length > 14) {
          return;
        }
        if (value.length > 0 && value.length < 14) {
          setFieldErrors(prev => ({
            ...prev,
            [name]: "Le SIRET doit contenir 14 chiffres"
          }));
        } else {
          setFieldErrors(prev => ({
            ...prev,
            [name]: null
          }));
        }
      }
      
      if (name === 'siren') {
        if (value.length > 9) {
          return;
        }
        if (value.length > 0 && value.length < 9) {
          setFieldErrors(prev => ({
            ...prev,
            [name]: "Le SIREN doit contenir 9 chiffres"
          }));
        } else {
          setFieldErrors(prev => ({
            ...prev,
            [name]: null
          }));
        }
      }
    }
    
    // Validation de l'email
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailRegex.test(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: "Format d'email invalide"
        }));
      } else {
        setFieldErrors(prev => ({
          ...prev,
          [name]: null
        }));
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const submitData = { ...formData };
    if (formData.statut === "assistante") {
      submitData.service_principal = null;
    }

    try {
      const result = await onSubmit(submitData);
      
      if (result && result.success) {
        // Handle successful response
        setCreatedUser(result.userData);
        setError(null);
        setFieldErrors({});
      } else if (result && result.error) {
        // Handle error response from API
        const errorData = result.error;
        
        // Reset any previous errors
        setFieldErrors({});
        
        // Handle specific error types
        if (errorData.errorType === 'DUPLICATE_EMAIL') {
          setFieldErrors(prev => ({
            ...prev,
            email: "Cet email est déjà utilisé par un autre utilisateur"
          }));
          setError("Cet email est déjà utilisé");
        } else if (errorData.errorType === 'DUPLICATE_TELEPHONE') {
          setFieldErrors(prev => ({
            ...prev,
            telephone: "Ce numéro de téléphone est déjà utilisé par un autre utilisateur"
          }));
          setError("Ce numéro de téléphone est déjà utilisé");
        } else if (errorData.field) {
          // Handle field-specific errors
          setFieldErrors(prev => ({
            ...prev,
            [errorData.field]: errorData.message || "Champ invalide"
          }));
          setError(errorData.message || "Veuillez corriger le formulaire");
        } else {
          // Handle general errors
          setError(errorData.message || "Erreur lors de la création de l'utilisateur");
        }
      }
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur:", error);
      setError("Erreur lors de la création de l'utilisateur");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    // Fermer le modal uniquement si on clique sur l'overlay et non sur le contenu
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  const handleCopyToClipboard = (text, type) => {
    // Ensure text is copied exactly as is, preserving case sensitivity
    if (text) {
      navigator.clipboard.writeText(text);
      setCopySuccess({
        ...copySuccess,
        [type]: true
      });
      
      // Reset copy success after 3 seconds
      setTimeout(() => {
        setCopySuccess(prev => ({
          ...prev,
          [type]: false
        }));
      }, 3000);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Nouveau profil</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {createdUser ? (
          <div className="modal-body">
            <div className="success-message">
              <p>Informations de connexion à communiquer:</p>
            </div>
            
            <div className="credentials-container">
              <div className="credential-group">
                <label>Identifiant:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={createdUser.identifier || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none', // Prevent any text transformation
                      letterSpacing: '0.5px' // Improve readability
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(createdUser.identifier, 'identifier')}
                  >
                    {copySuccess.identifier ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-group">
                <label>Mot de passe temporaire:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={createdUser.tempPassword || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none', // Prevent any text transformation
                      letterSpacing: '0.5px' // Improve readability
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(createdUser.tempPassword, 'password')}
                  >
                    {copySuccess.password ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-notice">
                ⚠️ Attention: L&apos;identifiant et le mot de passe sont sensibles à la casse.
                Veuillez les copier exactement comme affichés.
              </div>
            </div>

          </div>
        ) : (
          <div className="modal-body">
            <div className="form-section">
              <div className="form-group">
                <label className="required-field">Type d&apos;utilisateur</label>
                <select
                  name="statut"
                  value={formData.statut}
                  onChange={handleChange}
                  required
                  className={fieldErrors.statut ? "input-error" : ""}
                >
                  <option value="commercial">Commercial</option>

                </select>
                {fieldErrors.statut && <div className="field-error">{fieldErrors.statut}</div>}
              </div>
            </div>

            <div className="form-section">
              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">Prénom</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Jean"
                    className={fieldErrors.firstName ? "input-error" : ""}
                  />
                  {fieldErrors.firstName && <div className="field-error">{fieldErrors.firstName}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">Nom</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Dupont"
                    className={fieldErrors.lastName ? "input-error" : ""}
                  />
                  {fieldErrors.lastName && <div className="field-error">{fieldErrors.lastName}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="required-field">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Ex: jean.dupont@example.com"
                  className={fieldErrors.email ? "input-error" : ""}
                />
                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
              </div>
              
              <div className="form-group">
                <label className="required-field">Téléphone</label>
                <input
                  type="text"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 0123456789"
                  maxLength={10}
                  className={fieldErrors.telephone ? "input-error" : ""}
                />
                {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="required-field">Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Paris"
                  className={fieldErrors.ville ? "input-error" : ""}
                />
                {fieldErrors.ville && <div className="field-error">{fieldErrors.ville}</div>}
              </div>

              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">SIRET (14 chiffres)</label>
                  <input
                    type="text"
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    required
                    placeholder="Ex: 12345678901234"
                    maxLength={14}
                    className={fieldErrors.siret ? "input-error" : ""}
                  />
                  {fieldErrors.siret && <div className="field-error">{fieldErrors.siret}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">SIREN (9 chiffres)</label>
                  <input
                    type="text"
                    name="siren"
                    value={formData.siren}
                    onChange={handleChange}
                    required
                    placeholder="Ex: 123456789"
                    maxLength={9}
                    className={fieldErrors.siren ? "input-error" : ""}
                  />
                  {fieldErrors.siren && <div className="field-error">{fieldErrors.siren}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="required-field">Concession</label>
                <select
                  name="concessions_id"
                  value={formData.concessions_id}
                  onChange={handleChange}
                  required
                  className={fieldErrors.concessions_id ? "input-error" : ""}
                >
                  <option value="">Sélectionner une concession</option>
                  {concessions.map((concession) => (
                    <option key={concession.id} value={concession.id}>
                      {concession.nom}
                    </option>
                  ))}
                </select>
                {fieldErrors.concessions_id && <div className="field-error">{fieldErrors.concessions_id}</div>}
              </div>

              <div className="form-group">
                <label>Service Principal</label>
                <select
                  name="service_principal"
                  value={formData.service_principal}
                  onChange={handleChange}
                  className={fieldErrors.service_principal ? "input-error" : ""}
                >
                  <option value="">Sélectionner un service</option>
                  <option value="VO">{"Véhicules d'occasion"}</option>
                  <option value="VN">{"Véhicules neufs"}</option>
                  <option value="AV">{"Autres véhicules"}</option>
                </select>
                {fieldErrors.service_principal && <div className="field-error">{fieldErrors.service_principal}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {createdUser ? (
            <button
              type="button"
              className="button primary"
              onClick={onClose}
            >
              Fermer
            </button>
          ) : (
            <>
              {additionalButtons}
              <button type="button" className="button secondary" onClick={onClose}>
                Annuler
              </button>
              <button
                type="button"
                className="button primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Création en cours..." : "Créer"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AssistanteCreateCommercialForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  additionalButtons: PropTypes.node,
  user: PropTypes.object,
};

export default AssistanteCreateCommercialForm;